import { useEffect, useRef, useState } from 'react';
import { API_ORIGIN } from '@/app/constants';
import { useFetchStream } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { TaskInfo } from '@/app/types';
import { PROCESSING_TASK_STATES, TASK_COUNT_URL } from './constants';
import type { TaskState } from './types';

export const useProcessingCount = (types?: TaskInfo['type'][]) => {
  const [processingTaskCount, setProcessingTaskCount] = useState(0);
  const taskList = useStore((state) => state.taskList);
  const lastTaskListLength = useRef(taskList.length);

  // 用于标记是否需要保持 SSE 连接（当有进行中的任务时保持连接）
  const shouldKeepSSERef = useRef(false);

  const { run: getProcessingCount } = useFetchStream<{
    states: TaskState[];
    types?: TaskInfo['type'][];
  }>(TASK_COUNT_URL, {
    baseURL: API_ORIGIN,
    onMessage: (str) => {
      const dataObjects = str
        .split('data:')
        .map((d) => d.trim())
        .filter((d) => d);
      for (const dataString of dataObjects) {
        try {
          const json = JSON.parse(dataString);
          const count = json.total;
          if (typeof count === 'number') {
            setProcessingTaskCount(count);
            shouldKeepSSERef.current = count > 0;
          }
        } catch (error) {
          console.error('Error_from_get_task_count: ', error);
        }
      }
    },
    onClose: () => {
      // 如果还有进行中的任务，可能是异常断开，需要重新连接
      if (shouldKeepSSERef.current) {
        updateProcessingCount();
      }
    },
  });

  const updateProcessingCount = () => {
    getProcessingCount({
      states: PROCESSING_TASK_STATES,
      types,
    });
  };

  // 首次进入页面时，获取进行中的任务数量
  useEffect(() => {
    // 如果 > 0，会保持连接直到降为0，否则连接结束。
    updateProcessingCount();
  }, [types]);

  // 提交任务后（taskList[0].state 为 created），更新进行中的任务数量
  useEffect(() => {
    if (taskList.length - lastTaskListLength.current === 1 && PROCESSING_TASK_STATES.includes(taskList[0].state)) {
      updateProcessingCount();
    }
    lastTaskListLength.current = taskList.length;
  }, [taskList]);

  return {
    processingTaskCount,
  };
};
